import * as React from 'react';
import { PostingPlanCloneCalendarView, PostingPlanCloneCalendarViewProps } from './PostingPlanCloneCalendar.view';
import {
  CreateTimeslotMutation,
  TimeFormatSetting,
  useCreateTimeslotMutation,
  useUpdateTimeslotMutation
} from '../../../../models/graphql';
import { GetTimeslots_workspace_timeslots } from '../../../../models/legacy-graphql';
import { useContext } from 'react';
import { AppContext } from '../../../../providers/AppProvider';
import uuidGen from '../../../../helpers/uuidGen';
import { PostingPlanContext } from '../../../../providers/PostingPlanProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { showToast } from '../../../../helpers/showToast';
import { ApolloError } from '@apollo/client';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';

export type PostingPlanCloneCalendarProps = {
  timeFormat: TimeFormatSetting;
  exitHandler: CallableFunction;
  timeslot: GetTimeslots_workspace_timeslots;
};

export const PostingPlanCloneCalendarController = (props: PostingPlanCloneCalendarProps) => {
  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);
  const { timeslots, setTimeslotToClone } = useContext(PostingPlanContext);
  const [createTimeslot] = useCreateTimeslotMutation();
  const [updateTimeslot] = useUpdateTimeslotMutation();
  const { isDuplicateTimeslot, findMergeableTimeslot } = PostingPlanHelpers;

  const createTimeslotHandler = (timeslot: GetTimeslots_workspace_timeslots) => {
    const tempId = uuidGen();

    // Define the optimistic response
    const optimisticResponse: CreateTimeslotMutation['createTimeslot'] = {
      __typename: 'Timeslot',
      id: tempId,
      day: timeslot.day,
      time: String(timeslot.time),
      onboardingContent: timeslot.onboardingContent,
      category: timeslot.category,
      socialProfiles: timeslot.socialProfiles
    };

    // Execute the mutation with options
    createTimeslot({
      variables: {
        workspaceId: selectedWorkspaceId,
        input: {
          categoryId: timeslot.category.id,
          socialProfileIds: timeslot.socialProfiles.map(p => p.id),
          day: timeslot.day,
          time: timeslot.time
        }
      },
      optimisticResponse: {
        createTimeslot: optimisticResponse
      },
      update(cache, { data }) {
        cache.modify({
          id: `Workspace:${selectedWorkspaceId}`,
          fields: {
            timeslots(existingTimeslots = []) {
              // Add the new timeslot reference to the existing list
              return [...existingTimeslots, { __ref: `Timeslot:${data?.createTimeslot.id}` }];
            }
          }
        });
      }
    }).catch((error: ApolloError) => {
      if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
        setLimitReachedModalState({
          isOpen: true,
          type: 'timeslot'
        });
      } else {
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: 'There was a problem updating your Posting Plan.',
          detail: mappedError,
          severity: 'error'
        });
      }
    });
  };

  const mergeTimeslotHandler = (timeslot: GetTimeslots_workspace_timeslots) => {
    updateTimeslot({
      variables: {
        timeslotId: timeslot.id,
        input: {
          categoryId: timeslot.category.id,
          socialProfileIds: timeslot.socialProfiles.map(p => p.id),
          day: timeslot.day,
          time: timeslot.time
        }
      },
      optimisticResponse: {
        updateTimeslot: timeslot
      }
    })
      .then(_ => {
        showToast({
          summary: 'Existing timeslot has been updated.',
          detail: '',
          severity: 'success'
        });
      })
      .catch((error: ApolloError) => {
        const mappedError = remapApolloErrors(error);
        if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
          setLimitReachedModalState({
            isOpen: true,
            type: 'timeslot'
          });
        } else {
          showToast({
            summary: 'There was a problem updating your Posting Plan.',
            detail: mappedError,
            severity: 'error'
          });
        }
      });
  };

  const cloneTimeslotHandler = (timeslot: GetTimeslots_workspace_timeslots) => {
    if (!selectedWorkspaceId) return;

    // Check if absolute duplicate and return if it is
    if (isDuplicateTimeslot(timeslot, timeslots)) {
      showToast({
        severity: 'warn',
        summary: '',
        detail: 'You already have a matching Timeslot here'
      });
      return;
    }

    // Check to see if the timeslots need to be merged.
    const mergableTimeslot = findMergeableTimeslot(timeslot, timeslots);

    // If we are merging, send off to merge handler and return
    if (mergableTimeslot) {
      const timeslotToMerge: GetTimeslots_workspace_timeslots = {
        ...timeslot,
        ...mergableTimeslot,
        socialProfiles: Array.from(
          new Set([
            ...timeslot.socialProfiles.map(({ id }) => id),
            ...mergableTimeslot.socialProfiles.map(({ id }) => id)
          ])
        ).map(id => ({ id }))
      };

      mergeTimeslotHandler(timeslotToMerge);

      return;
    }

    createTimeslotHandler(timeslot);
  };

  useDidMount(() => {
    setTimeslotToClone(props.timeslot);
  });

  const viewProps: PostingPlanCloneCalendarViewProps = {
    ...props,
    cloneTimeslotHandler
  };

  return <PostingPlanCloneCalendarView {...viewProps} />;
};
